// HT-1 · 修正内容の作成（保存前に「システムの理解」を必ず提示する）
import { useState } from 'react';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Check, Plus, Trash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { LEARN, authHeader, detailOf, Panel, Pill, Empty, Spinner, ROLE_JA } from './shared';

function Understanding({ view, onSave, onSaveDocumentOnly, onEdit, onCancel, busy }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4"
      role="dialog" aria-modal="true" aria-label="理解の確認"
      data-testid="teach-understanding-dialog">
      <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-lg border border-zinc-200 bg-white p-4">
        <h3 className="text-base font-semibold text-zinc-900">{view.notice_ja}</h3>
        <dl className="pt-3 space-y-1.5 text-[12px] text-zinc-800">
          <div><dt className="inline font-medium">対象: </dt>
            <dd className="inline">{view.document_title || view.document_id}</dd></div>
          <div><dt className="inline font-medium">場所: </dt>
            <dd className="inline">{view.place_ja}</dd></div>
          <div><dt className="inline font-medium">読み取った文字: </dt>
            <dd className="inline">{view.observed_value || '（候補なし）'}</dd></div>
          <div><dt className="inline font-medium">項目: </dt>
            <dd className="inline">{view.canonical_field_label_ja}</dd></div>
          <div><dt className="inline font-medium">種類: </dt>
            <dd className="inline">{view.entity_type_label_ja}</dd></div>
          <div><dt className="inline font-medium">修正: </dt>
            <dd className="inline">{view.corrected_value || '—'}</dd></div>
          <div><dt className="inline font-medium">この書類への反映: </dt>
            <dd className="inline">{view.document_effect_ja}</dd></div>
          <div><dt className="inline font-medium">今後への反映: </dt>
            <dd className="inline">{view.creates_learning_candidate
              ? view.future_effect_ja : '今後の読み取りには影響しません。'}</dd></div>
        </dl>
        <p className="flex flex-wrap gap-1.5 pt-2">
          <Pill tone={view.risk === 'high' ? 'rose' : view.risk === 'medium' ? 'amber' : 'teal'}>
            リスク: {view.risk_label_ja}
          </Pill>
          {(view.profiles_affected_labels_ja || []).map((p) => <Pill key={p}>{p}</Pill>)}
          <Pill tone={view.real_document_status?.real_document_validated ? 'teal' : 'amber'}
            testId="teach-understanding-real-status">
            {view.real_document_status?.label_ja}
          </Pill>
        </p>
        {view.needs_more_examples && (
          <p className="pt-2 text-[11.5px] text-amber-800" data-testid="teach-needs-examples">
            {view.needs_more_examples_ja}
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-2 pt-4">
          <Button variant="outline" className="h-10 text-[12px]" onClick={onCancel}
            data-testid="teach-understanding-cancel">キャンセル</Button>
          <Button variant="outline" className="h-10 text-[12px]" onClick={onEdit}
            data-testid="teach-understanding-edit">内容を修正</Button>
          <Button variant="outline" className="h-10 text-[12px]" onClick={onSaveDocumentOnly}
            disabled={busy} data-testid="teach-understanding-document-only">
            この書類だけ修正
          </Button>
          <Button className="h-10 bg-teal-700 text-[12.5px] hover:bg-teal-800" onClick={onSave}
            disabled={busy} data-testid="teach-understanding-save">
            {busy ? <Spinner /> : <Check size={13} className="mr-1" />}
            <span className="ml-1">この内容で保存</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

export function Correct({ session, meta, data, onBack, onNext, onChanged }) {
  const [type, setType] = useState('responsibility');
  const [role, setRole] = useState('designed_by');
  const [value, setValue] = useState('');
  const [explanation, setExplanation] = useState('');
  const [createProposal, setCreateProposal] = useState(false);
  const [positive, setPositive] = useState('');
  const [negative, setNegative] = useState('');
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);
  const types = meta?.correction_types || [];
  const units = session.selected_source_units || [];
  const sid = session.teaching_session_id;

  const body = (withProposal) => ({
    correction_type: type, canonical_field_key: role,
    source_field: (units.find((u) => u.unit_id === positive) || {}).source_field || null,
    observed_value: (data.raw || {})[role]
      || (data.stamp_candidates || {})[role]?.candidate || null,
    corrected_value: type === 'exclude' ? null : value.trim() || null,
    entity_type: (data.field_sources || {})[role]?.entity_type || null,
    explanation, create_proposal: withProposal,
    positive_example_unit_id: positive || null,
    negative_example_unit_id: negative || null,
  });

  const preview = async () => {
    if (type !== 'exclude' && !value.trim()) { toast.error('修正内容を入力してください'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${LEARN}/teaching/sessions/${sid}/preview`,
        body(createProposal), { headers: authHeader() });
      setView(r.data.understanding);
    } catch (e) { toast.error(detailOf(e, '内容を確認できませんでした')); } finally { setBusy(false); }
  };

  const save = async (withProposal) => {
    setBusy(true);
    try {
      await axios.post(`${LEARN}/teaching/sessions/${sid}/corrections`,
        body(withProposal), { headers: authHeader() });
      toast.success(withProposal
        ? '修正内容と学習候補の作成予定を保存しました'
        : 'この書類の修正内容を保存しました');
      setView(null); setValue(''); setExplanation(''); setCreateProposal(false);
      onChanged?.();
    } catch (e) { toast.error(detailOf(e, '保存できませんでした')); } finally { setBusy(false); }
  };

  const remove = async (cid) => {
    try {
      await axios.delete(`${LEARN}/teaching/sessions/${sid}/corrections/${cid}`,
        { headers: authHeader() });
      onChanged?.();
    } catch (e) { toast.error(detailOf(e, '取り消せませんでした')); }
  };

  const corrections = session.corrections || [];
  return (
    <div className="space-y-4">
      <Panel title="間違いを修正して説明する" testId="teach-step3"
        hint="この書類への反映と、今後の読み取りへの反映は別に扱います。保存の前に内容を確認します。">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div className="space-y-2">
            <label className="block text-[11.5px] text-zinc-600" htmlFor="teach-correction-type">
              修正の種類
            </label>
            <select id="teach-correction-type" value={type}
              onChange={(e) => setType(e.target.value)}
              className="h-10 w-full rounded-md border border-zinc-200 px-2 text-[12.5px]"
              data-testid="teach-correction-type">
              {types.map((c) => <option key={c.key} value={c.key}>{c.label_ja}</option>)}
            </select>
            <label className="block text-[11.5px] text-zinc-600" htmlFor="teach-correction-role">
              対象の項目
            </label>
            <select id="teach-correction-role" value={role}
              onChange={(e) => setRole(e.target.value)}
              className="h-10 w-full rounded-md border border-zinc-200 px-2 text-[12.5px]"
              data-testid="teach-correction-role">
              {Object.entries(ROLE_JA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {type !== 'exclude' && (
              <input value={value} onChange={(e) => setValue(e.target.value)}
                placeholder="正しい内容（例: 小山）" aria-label="修正内容"
                className="h-10 w-full rounded-md border border-zinc-200 px-2 text-[12.5px]"
                data-testid="teach-correction-value" />
            )}
            <textarea value={explanation} onChange={(e) => setExplanation(e.target.value)}
              placeholder="なぜそう読むのかを説明（例: 設計欄の丸印です）" aria-label="説明"
              className="h-16 w-full rounded-md border border-zinc-200 px-2 py-1 text-[12.5px]"
              data-testid="teach-correction-explanation" />
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <select value={positive} onChange={(e) => setPositive(e.target.value)}
                aria-label="正しい例の領域"
                className="h-10 rounded-md border border-zinc-200 px-2 text-[12px]"
                data-testid="teach-correction-positive">
                <option value="">— 正しい例の領域 —</option>
                {units.map((u) => (
                  <option key={u.unit_id} value={u.unit_id}>
                    {u.region_role_label_ja}{u.source_field ? ` / ${u.source_field}` : ''}
                  </option>
                ))}
              </select>
              <select value={negative} onChange={(e) => setNegative(e.target.value)}
                aria-label="誤りやすい例の領域"
                className="h-10 rounded-md border border-zinc-200 px-2 text-[12px]"
                data-testid="teach-correction-negative">
                <option value="">— 誤りやすい例の領域 —</option>
                {units.map((u) => (
                  <option key={u.unit_id} value={u.unit_id}>
                    {u.region_role_label_ja}{u.source_field ? ` / ${u.source_field}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex min-h-[36px] items-center gap-2 text-[12px] text-zinc-700">
              <input type="checkbox" checked={createProposal}
                onChange={(e) => setCreateProposal(e.target.checked)}
                className="h-4 w-4" data-testid="teach-correction-candidate" />
              学習候補も作成する（承認と検証を通るまで使用されません）
            </label>
            <Button className="h-10 bg-teal-700 text-[12.5px] hover:bg-teal-800"
              onClick={preview} disabled={busy} data-testid="teach-correction-preview">
              {busy ? <Spinner /> : <ArrowRight size={13} className="mr-1" />}
              <span className="ml-1">内容を確認する</span>
            </Button>
          </div>
          <div className="rounded-md border border-zinc-200 p-3"
            data-testid="teach-correction-current">
            <p className="text-[12px] font-medium text-zinc-800">現在の読み取り</p>
            <p className="pt-0.5 text-[12.5px] text-zinc-900">
              {(data.raw || {})[role] || (data.stamp_candidates || {})[role]?.candidate
                || '（候補なし）'}
            </p>
            <p className="pt-2 text-[12px] font-medium text-zinc-800">読み取り元</p>
            <p className="pt-0.5 text-[11.5px] text-zinc-600">
              {(data.field_sources || {})[role]?.title_block_field || '—'} ·
              {' '}{(data.field_sources || {})[role]?.reason_ja || '—'}
            </p>
            <p className="pt-2 text-[12px] font-medium text-zinc-800">実書類での検証</p>
            <p className="pt-0.5 text-[11.5px] text-zinc-600"
              data-testid="teach-real-document-status">
              {meta?.real_document_status?.label_ja} ·
              {' '}{meta?.real_document_status?.note_ja}
            </p>
          </div>
        </div>
      </Panel>

      <Panel title="保存した修正内容" testId="teach-corrections">
        {corrections.length === 0 ? (
          <Empty testId="teach-corrections-empty">修正内容はまだありません。</Empty>
        ) : (
          <div className="space-y-1.5">
            {corrections.map((c) => (
              <div key={c.correction_id}
                className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-zinc-200 px-3 py-2"
                data-testid={`teach-correction-row-${c.correction_id}`}>
                <div className="min-w-0">
                  <p className="text-[12.5px] text-zinc-900">
                    {c.understanding?.correction_label_ja}
                    <span className="ml-2 text-[11.5px] text-zinc-600">
                      {c.understanding?.canonical_field_label_ja}
                    </span>
                    {c.corrected_value && <span className="ml-2 text-[11.5px]">→ {c.corrected_value}</span>}
                  </p>
                  <p className="flex flex-wrap gap-1.5 pt-1">
                    {c.apply_to_document && <Pill tone="teal">この書類に反映</Pill>}
                    {c.create_proposal && <Pill tone="amber">学習候補を作成</Pill>}
                    {c.understanding?.needs_more_examples && <Pill tone="rose">例が不足</Pill>}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="h-9 text-[11px]"
                  onClick={() => remove(c.correction_id)}
                  data-testid={`teach-correction-remove-${c.correction_id}`}>
                  <Trash size={11} className="mr-1" />取り消す
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-3">
          <Button variant="outline" className="h-10 text-[12px]" onClick={onBack}
            data-testid="teach-step3-back">
            <ArrowLeft size={13} className="mr-1" />読み取り結果に戻る
          </Button>
          <Button className="h-10 bg-teal-700 text-[12.5px] hover:bg-teal-800" onClick={onNext}
            disabled={corrections.length === 0} data-testid="teach-step3-next">
            次へ<ArrowRight size={13} className="ml-1" />
          </Button>
        </div>
      </Panel>

      {view && (
        <Understanding view={view} busy={busy}
          onSave={() => save(createProposal)}
          onSaveDocumentOnly={() => save(false)}
          onEdit={() => setView(null)} onCancel={() => setView(null)} />
      )}
    </div>
  );
}

export function Submit({ session, onBack, onDone, onGo }) {
  const [busy, setBusy] = useState(false);
  const corrections = session.corrections || [];
  const docItems = corrections.filter((c) => c.apply_to_document);
  const candidates = corrections.filter((c) => c.create_proposal);

  const submit = async (createProposals) => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${LEARN}/teaching/sessions/${session.teaching_session_id}/submit`,
        { apply_document: true, create_proposals: createProposals },
        { headers: authHeader() });
      toast.success(r.data.notice_ja);
      (r.data.skipped || []).forEach((s) => toast.warning(s.reason_ja));
      onDone();
    } catch (e) { toast.error(detailOf(e, '保存できませんでした')); } finally { setBusy(false); }
  };

  return (
    <Panel title="内容を確認して保存" testId="teach-step4"
      hint="この書類の修正はすぐ反映されます。学習候補は承認と検証を通ってから使われます。">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="rounded-md border border-zinc-200 p-3" data-testid="teach-summary-document">
          <p className="text-[12px] font-medium text-zinc-800">この書類で修正する内容</p>
          {docItems.length === 0
            ? <p className="pt-1 text-[11.5px] text-zinc-500">この書類の値は変更しません。</p>
            : docItems.map((c) => (
              <p key={c.correction_id} className="pt-1 text-[11.5px] text-zinc-700">
                ・{c.understanding?.document_effect_ja}
              </p>
            ))}
        </div>
        <div className="rounded-md border border-amber-200 bg-amber-50/40 p-3"
          data-testid="teach-summary-learning">
          <p className="text-[12px] font-medium text-zinc-800">今後の読み取りに使う候補</p>
          {candidates.length === 0
            ? <p className="pt-1 text-[11.5px] text-zinc-500">学習候補は作成しません。</p>
            : candidates.map((c) => (
              <p key={c.correction_id} className="pt-1 text-[11.5px] text-zinc-700">
                ・{c.understanding?.pattern_label_ja}
                {c.understanding?.needs_more_examples ? '（誤りやすい例が必要です）' : ''}
              </p>
            ))}
          <p className="pt-2 text-[11px] text-zinc-600">
            承認するまで、ほかの書類の読み取りは変わりません。
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 pt-3">
        <Button variant="outline" className="h-10 text-[12px]" onClick={onBack}
          data-testid="teach-step4-back">
          <ArrowLeft size={13} className="mr-1" />修正に戻る
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          {candidates.length > 0 && (
            <Button variant="outline" className="h-10 text-[12px]" disabled={busy}
              onClick={() => submit(true)} data-testid="teach-save-with-candidates">
              <Plus size={12} className="mr-1" />学習候補も作成
            </Button>
          )}
          <Button className="h-10 bg-teal-700 text-[12.5px] hover:bg-teal-800" disabled={busy}
            onClick={() => submit(false)} data-testid="teach-save">
            {busy ? <Spinner /> : <Check size={13} className="mr-1" />}
            <span className="ml-1">この書類を修正</span>
          </Button>
        </div>
      </div>
      <p className="pt-2 text-[11.5px] text-zinc-500">
        作成した学習候補は「承認する」で確認できます。
        <button type="button" onClick={() => onGo('approve')}
          className="ml-1 min-h-[32px] text-teal-700 hover:underline"
          data-testid="teach-goto-approve">承認する画面へ</button>
      </p>
    </Panel>
  );
}
